import { motion } from "framer-motion";
import {
 Container, Section, SectionHeading, PageHero, BtnPrimary, BtnGhost, TrustStrip, StatBar, CtaBanner,
 CheckCircle2, ArrowRight,
} from "../components/common";
import { useSeo, breadcrumbLd, ORIGIN } from "../lib/seo";

const SHOP = "https://images.pexels.com/photos/4116170/pexels-photo-4116170.jpeg?auto=compress&cs=tinysrgb&w=1200";
const MECH = "https://images.pexels.com/photos/8985457/pexels-photo-8985457.jpeg?auto=compress&cs=tinysrgb&w=1200";

const VALUES = [
 ["Honesty First", "We recommend only what your vehicle actually needs, and we show you the proof. If it can wait, we'll tell you."],
 ["Quality Workmanship", "ASE Master Certified technicians, factory procedures, and premium parts on every job, big or small."],
 ["Fair, Transparent Pricing", "Clear written estimates before we begin. You approve every dollar, no surprise charges, ever."],
 ["Treating You Like Family", "Many of our customers have trusted us for two decades. We earn that loyalty one honest repair at a time."],
];

export default function About() {
 useSeo({
 title: "About RPM Auto Repair | ASE Certified Mechanics in Riverside Since 1995",
 description: "Meet RPM Auto Repair, a family-owned, full-service team of ASE Master Certified mechanics in Riverside, CA since 1995. All makes and models, luxury & exotic, collision & body work, with a nationwide warranty.",
 path: "/about", image: SHOP,
 jsonLd: [
 { "@context": "https://schema.org", "@type": "AboutPage", name: "About RPM Auto Repair", url: `${ORIGIN}/about` },
 breadcrumbLd([{ name: "Home", path: "/" }, { name: "About", path: "/about" }]),
 ],
 });
 return (
 <>
 <PageHero eyebrow="Family Owned Since 1995" title="Three Decades of Honest Service"
 subtitle="RPM Auto Repair was built on a handshake promise: treat every customer honestly and do the job right. That promise still drives everything we do."
 image={SHOP} crumbs={[{ name: "Home", path: "/" }, { name: "About", path: "/about" }]} />
 <TrustStrip />

 <Section>
 <Container className="grid items-center gap-12 lg:grid-cols-2">
 <div>
 <SectionHeading eyebrow="Our Story" title="Big-Dealer Quality, Family-Shop Heart" />
 <p className="leading-relaxed text-muted-foreground">
 When RPM Auto Repair opened its doors in Riverside in 1995, the goal was simple: give local drivers a place they could actually trust. No pressure, no confusing jargon, no repairs they didn't need, just skilled, certified work at a fair price.
 </p>
 <p className="mt-4 leading-relaxed text-muted-foreground">
 Nearly thirty years later, that hasn't changed. We've grown into one of Riverside County's most trusted full-service shops, caring for everything from daily commuters, family SUVs, work fleets and diesel trucks to high-end luxury and exotic vehicles like Mercedes-Benz, BMW, Audi, Porsche and Tesla, and we handle collision and body repair too. What keeps our customers coming back for decades is the same thing that brought them in: honesty you can feel and workmanship you can rely on.
 </p>
 <p className="mt-4 leading-relaxed text-muted-foreground">
 As a NAPA AutoCare Center, our repairs are backed by a nationwide 24-month/24,000-mile warranty, proof that we stand behind our work long after you leave the shop. Conveniently located right off the 215 freeway on E Alessandro Blvd, our ASE-certified mechanics are an easy drive from anywhere in Riverside County.
 </p>
 <div className="mt-7 flex flex-wrap gap-4">
 <BtnPrimary to="/services">Our Services <ArrowRight size={16} /></BtnPrimary>
 <BtnGhost to="/schedule-appointment">Book a Visit</BtnGhost>
 </div>
 </div>
 <div className="grid grid-cols-2 gap-4">
 <img src={SHOP} alt="Inside the RPM Auto Repair service bay in Riverside" loading="lazy" className="col-span-2 h-56 w-full rounded-xl border border-white/10 object-cover" />
 <img src={MECH} alt="ASE certified technician inspecting an engine" loading="lazy" className="h-44 w-full rounded-xl border border-white/10 object-cover" />
 <div className="flex flex-col justify-center rounded-xl border border-primary/30 bg-primary/10 p-5">
 <div className="font-display text-4xl text-primary">30+</div>
 <div className="mt-1 text-sm text-white">Years serving Riverside County with honest care</div>
 </div>
 </div>
 </Container>
 </Section>

 <Section className="bg-steel/40">
 <Container>
 <SectionHeading center eyebrow="What We Stand For" title="Values That Drive Every Repair" />
 <div className="grid gap-6 sm:grid-cols-2">
 {VALUES.map(([t, d], i) => (
 <motion.div key={t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
 transition={{ duration: 0.4, delay: i * 0.08 }}
 className="flex gap-4 rounded-xl border border-white/10 bg-steel p-6">
 <CheckCircle2 className="mt-0.5 shrink-0 text-primary" size={22} />
 <div>
 <h3 className="font-display text-xl text-white">{t}</h3>
 <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{d}</p>
 </div>
 </motion.div>
 ))}
 </div>
 </Container>
 </Section>

 <Section>
 <Container><StatBar /></Container>
 </Section>

 <CtaBanner title="Experience the RPM Difference" />
 </>
 );
}
